import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { StoredIdentity } from "@/lib/crypto/keystore";

export function useIdentityKeys() {
  const { user } = useAuth();
  const [identity, setIdentity] = useState<StoredIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { setIdentity(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    // Lazy-load the crypto chain (libsodium ~150KB) only after auth.
    // Defer to idle so it never blocks the initial render on mobile.
    const run = () => import("@/lib/crypto/identity").then(({ ensureIdentity }) =>
      ensureIdentity(user.id)
        .then((id) => { if (!cancelled) { setIdentity(id); setError(null); } })
        .catch((e) => { if (!cancelled) setError(String(e?.message || e)); })
        .finally(() => { if (!cancelled) setLoading(false); })
    );
    const w: any = typeof window !== "undefined" ? window : null;
    const idleId = w?.requestIdleCallback ? w.requestIdleCallback(run, { timeout: 2000 }) : setTimeout(run, 0);
    return () => { cancelled = true; };
  }, [user?.id]);

  const rotate = useCallback(async () => {
    if (!user) return null;
    const { rotateIdentity } = await import("@/lib/crypto/identity");
    const next = await rotateIdentity(user.id);
    setIdentity(next);
    return next;
  }, [user?.id]);

  return { identity, loading, error, rotate, setIdentity };
}